import apiService from './api';
import secureTokenService from './secure-token-service';
import { ErrorHandler } from '@/lib/error-handler';
import type {
  UserEntity,
  AuthLoginApiResponse,
  AuthRegisterApiResponse,
  AuthRegisterApiRequest,
  AuthRefreshTokenApiResponse,
  UserProfileApiResponse,
  UpdateUserProfileApiRequest,
  AuthDeleteAccountApiResponse,
} from '@trackv16/types';

class AuthService {
  constructor() {
    // Migrate existing tokens on initialization
    this.migrateTokens();
  }

  private async migrateTokens(): Promise<void> {
    try {
      await secureTokenService.migrateFromLocalStorage();
    } catch {
      // Silent migration failure - tokens will be requested on next login
    }
  }

  async login(email: string, password: string): Promise<AuthLoginApiResponse> {
    try {
      const response = await apiService.post<AuthLoginApiResponse>(
        '/auth/login',
        {
          email,
          password,
        }
      );

      // Store tokens securely
      if (response.token) {
        secureTokenService.setAccessToken(response.token);
        if (response.refreshToken) {
          // Calculate expiry (typically 7 days for refresh tokens)
          await secureTokenService.setRefreshToken(
            response.refreshToken,
            7 * 24 * 60 * 60
          );
        }
      }

      return response;
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  async register(
    data: AuthRegisterApiRequest
  ): Promise<AuthRegisterApiResponse> {
    try {
      const response = await apiService.post<AuthRegisterApiResponse>(
        '/auth/register',
        data
      );

      // Store tokens securely
      if (response.token) {
        secureTokenService.setAccessToken(response.token);
        if (response.refreshToken) {
          // Calculate expiry (typically 7 days for refresh tokens)
          await secureTokenService.setRefreshToken(
            response.refreshToken,
            7 * 24 * 60 * 60
          );
        }
      }

      return response;
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  async logout(skipApiCall = false): Promise<void> {
    // Skip API call if we're already handling a 401 or if explicitly requested
    if (!skipApiCall) {
      try {
        await apiService.post('/auth/logout');
      } catch {
        // Silent logout error - clear tokens anyway
      }
    }
    // Always clear tokens locally
    await secureTokenService.clearAll();
  }

  async getCurrentUser(): Promise<UserEntity> {
    try {
      const response =
        await apiService.get<UserProfileApiResponse>('/auth/profile');
      return response.user;
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  async updateProfile(data: UpdateUserProfileApiRequest): Promise<UserEntity> {
    try {
      const response = await apiService.patch<UserProfileApiResponse>(
        '/auth/profile',
        data
      );
      return response.user;
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  async refreshToken(): Promise<string> {
    try {
      const refreshToken = await secureTokenService.getRefreshToken();
      if (!refreshToken) {
        throw ErrorHandler.transformError(
          new Error('No refresh token available')
        );
      }

      const response = await apiService.post<AuthRefreshTokenApiResponse>(
        '/auth/refresh',
        {
          refresh_token: refreshToken,
        }
      );

      // Update tokens securely
      secureTokenService.setAccessToken(response.token);
      if (response.refreshToken) {
        await secureTokenService.setRefreshToken(
          response.refreshToken,
          7 * 24 * 60 * 60
        );
      }
      return response.token;
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    try {
      await apiService.post('/auth/forgot-password', { email });
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  async resetPassword(token: string, password: string): Promise<void> {
    try {
      await apiService.post('/auth/reset-password', {
        token,
        password,
      });
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  async resendVerificationEmail(email: string): Promise<void> {
    try {
      await apiService.post('/auth/resend-verification', { email });
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }

  isAuthenticated(): boolean {
    return secureTokenService.hasAccessToken();
  }

  getAccessToken(): string | null {
    return secureTokenService.getAccessToken();
  }

  async getRefreshToken(): Promise<string | null> {
    return await secureTokenService.getRefreshToken();
  }

  async deleteAccount(): Promise<void> {
    try {
      const response =
        await apiService.delete<AuthDeleteAccountApiResponse>('/auth/account');

      if (!response.success) {
        throw ErrorHandler.transformError(
          new Error(response.message || 'Failed to delete account')
        );
      }

      // Clear tokens after successful deletion
      await secureTokenService.clearAll();
    } catch (error) {
      throw ErrorHandler.transformError(error);
    }
  }
}

export default new AuthService();
